import { useEffect, useRef, useState } from "react";
import { TheProof, TheDMs, CandidateProfile, Opportunity } from "../types";
import { ChatThreadMessage, ModalKind } from "../components/chat/types";
import { seedThread } from "./useChatThreadSeed";
import { useChatIntake } from "./useChatIntake";
import { msgId, nowIso } from "../utils/chat";
import { extractCompanies, extractRoleTitle } from "../conversation/inference";

export function useChatThread(
  opportunity: Opportunity | undefined,
  proof: TheProof[],
  profile: CandidateProfile | undefined,
  dms: TheDMs[],
  options: {
    isIntakeMode?: boolean;
    onIntakeComplete?: (answers: Record<string, any>) => void;
  } = {},
) {
  const [messages, setMessages] = useState<ChatThreadMessage[]>([]);
  const [openModal, setOpenModal] = useState<ModalKind>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const seededOpportunityId = useRef<string | undefined>(undefined);

  const { isProcessing, currentStep, handleControlSubmit, handleBacktrack } = useChatIntake(
    options,
    messages,
    setMessages,
    pushSystemMessage,
  );

  useEffect(() => {
    if (options.isIntakeMode) return;
    if (!opportunity) {
      setMessages([]);
      seededOpportunityId.current = undefined;
      return;
    }
    if (seededOpportunityId.current === opportunity.opportunity_id) return;
    seededOpportunityId.current = opportunity.opportunity_id;
    setMessages(seedThread(opportunity, proof, profile, dms));
  }, [opportunity, options.isIntakeMode, dms, profile, proof]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  function handleControlChange(messageId: string, value: unknown) {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, controlValue: value, controlError: null } : m)),
    );
    const msg = messages.find((m) => m.id === messageId);
    if (msg?.type === "ai" && msg.controlStep?.control === "choice") {
      handleControlSubmit(messageId, value);
    }
  }

  function sendUserMessage(text: string) {
    if (!text.trim()) return;
    const userMsg: ChatThreadMessage = {
      type: "user",
      id: msgId("user"),
      text: text.trim(),
      timestamp: nowIso(),
    };
    setMessages((prev) => [...prev, userMsg]);
    if (options.isIntakeMode && currentStep) handleIntakeMessage(text.trim());
  }

  function handleIntakeMessage(text: string) {
    const lower = text.toLowerCase();
    if (lower.includes("go back") || lower.includes("previous step") || lower.includes("undo")) {
      handleBacktrack();
      return;
    }

    const companies = extractCompanies(text);
    const role = extractRoleTitle(text);

    if (currentStep?.id === "signalText" || currentStep?.id === "signalUrl") {
      handleControlSubmit(messages[messages.length - 1].id, text);
    } else {
      if (companies.length || role) {
        pushSystemMessage(`Noted ${companies[0] || ""} ${role ? `for ${role}` : ""}.`);
      }
      handleControlSubmit(messages[messages.length - 1].id, text);
    }
  }

  function pushSystemMessage(text: string) {
    setMessages((prev) => [
      ...prev,
      { type: "system", id: msgId("sys"), text, timestamp: nowIso() },
    ]);
  }

  return {
    messages,
    openModal,
    setOpenModal,
    sendUserMessage,
    pushSystemMessage,
    handleControlChange,
    handleControlSubmit,
    threadEndRef,
    isProcessing,
  };
}
